import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import React, { useEffect, useState } from 'react';

export interface TooltipWebV3Props extends TooltipProps {
  marginOffset?: number | string;
  hideOnScroll?: boolean;
  variant?: 'dark' | 'light';
  minWidth?: string;
  isTaskPanel?: boolean;
}

// ✅ Define the wrapper OUTSIDE the component
const TooltipTargetWrapper = styled('span')({
  display: 'inline-block',
  font: 'inherit',
  color: 'inherit',
  lineHeight: 'inherit',
});

const StyledTooltip = styled(({ className, ...props }: TooltipWebV3Props) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(
  ({
    marginOffset = 4,
    variant = 'dark',
    minWidth,
    isTaskPanel = false,
  }: {
    marginOffset?: number | string;
    variant?: 'dark' | 'light';
    minWidth?: undefined;
    isTaskPanel?: boolean;
  }) => ({
    zIndex: 1600,
    [`& .${tooltipClasses.tooltip}`]: {
      padding: '8px 12px',
      maxWidth: 300,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      minWidth: variant === 'dark' && minWidth === undefined ? '100px' : 'auto',
      margin: '0 !important',
      minHeight: '32px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      backgroundColor: variant === 'dark' ? '#303742' : '#ffffff',
      color: variant === 'dark' ? '#ffffff' : '#000000',
      borderRadius: 4,
      boxShadow:
        variant === 'light'
          ? '0 0 0 1px rgba(0,0,0,0.04), 0 4px 8px rgba(0,0,0,0.08)'
          : 'none',
    },
    [`& .${tooltipClasses.arrow}`]: {
      display: 'none',
    },
    '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
      marginTop: `${marginOffset}px !important`,
    },
    '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
      marginBottom: '0px !important',
    },
    '&[data-popper-placement*="left"] .MuiTooltip-tooltip': {
      marginRight: '0px !important',
    },
    '&[data-popper-placement*="right"] .MuiTooltip-tooltip': {
      marginLeft: '0px !important',
    },
    '.MuiTooltip-tooltip': {
      marginRight: isTaskPanel ? '-8px !important' : undefined,
    },
  })
);

export const TooltipWebV3: React.FC<TooltipWebV3Props> = (props) => {
  const {
    hideOnScroll = true,
    onOpen,
    onClose,
    open: openProp,
    isTaskPanel = false,
    children,
    ...restProps
  } = props;

  const isControlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = isControlled ? (openProp as boolean) : openState;

  useEffect(() => {
    if (!hideOnScroll) return;

    const handleScroll = () => {
      if (!isControlled) {
        setOpenState(false);
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [hideOnScroll, isControlled]);

  const handleOpen: TooltipProps['onOpen'] = (event) => {
    if (!isControlled) {
      setOpenState(true);
    }
    onOpen?.(event);
  };

  const handleClose: TooltipProps['onClose'] = (event) => {
    if (!isControlled) {
      setOpenState(false);
    }
    onClose?.(event);
  };

  return (
    <StyledTooltip
      open={open}
      isTaskPanel={isTaskPanel}
      onOpen={handleOpen}
      onClose={handleClose}
      {...restProps}>
      {/* ✅ Use wrapper as a stable anchor element */}
      <TooltipTargetWrapper>{children}</TooltipTargetWrapper>
    </StyledTooltip>
  );
};
